import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FaAward, FaCalendarAlt, FaVideo } from 'react-icons/fa';
import { axiosInstance as axios } from '../../context/AuthContext';
import { FamilyPilotSurveyDialog } from './FamilyPilotSurveyDialog';

function PresentationRow({ p, onRegister, onRate }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-white/70 rounded-lg p-3" data-testid={`pilot-pres-${p.presentation_id}`}>
      <div>
        <p className="font-semibold text-gray-800">{p.title}</p>
        <p className="text-sm text-gray-600"><FaCalendarAlt className="inline mr-1" />{p.date} · {p.time} · with {p.presenter_name}</p>
        {p.description && <p className="text-sm text-gray-500 mt-1">{p.description}</p>}
      </div>
      <div className="flex gap-2">
        {!p.registered && <button onClick={() => onRegister(p)} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium" data-testid="pilot-pres-register-btn">Register</button>}
        {p.registered && p.meeting_link && <a href={p.meeting_link} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium inline-flex items-center gap-1" data-testid="pilot-pres-join-link"><FaVideo />Join</a>}
        {p.registered && !p.survey_done && p.can_rate && <button onClick={() => onRate(p)} className="px-4 py-2 border border-emerald-600 text-emerald-700 rounded-lg text-sm font-medium" data-testid="pilot-pres-rate-btn">Rate it</button>}
        {p.survey_done && <span className="text-sm text-emerald-700 font-medium" data-testid="pilot-pres-rated">Thanks for your feedback</span>}
      </div>
    </div>
  );
}

export function FamilyPilotCard() {
  const [s, setS] = useState(null);
  const [rating, setRating] = useState(null);
  const load = useCallback(() => axios.get('/api/pilot/family/status').then(r => setS(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  if (!s?.open || (!s.joined && s.spots_left === 0)) return null;

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Something went wrong'); }
  };
  const join = () => act(() => axios.post('/api/pilot/family/join'), 'Welcome to the pilot!');
  const register = (p) => act(() => axios.post(`/api/pilot/family/presentations/${p.presentation_id}/register`), "You're registered");

  return (
    <div className="mb-8 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 p-6" data-testid="family-pilot-card">
      <p className="inline-flex items-center gap-2 text-emerald-700 text-xs font-bold uppercase tracking-wide"><FaAward /> Founding Family Pilot</p>
      {!s.joined ? (
        <div className="flex flex-wrap items-center justify-between gap-4 mt-1">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Free educational sessions with vetted professionals</h3>
            <p className="text-sm text-gray-600">Join a small pilot: attend a short presentation from our founding attorney or financial planner and tell us what you think. {s.spots_left} spots left.</p>
          </div>
          <button onClick={join} className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg font-semibold" data-testid="family-pilot-join-btn">Join the pilot</button>
        </div>
      ) : (
        <div className="mt-1 space-y-3">
          <h3 className="text-lg font-bold text-gray-800">You're in the pilot — thank you!</h3>
          {s.presentations.length === 0
            ? <p className="text-sm text-gray-600" data-testid="family-pilot-no-pres">Presentations will appear here soon.</p>
            : s.presentations.map(p => <PresentationRow key={p.presentation_id} p={p} onRegister={register} onRate={setRating} />)}
        </div>
      )}
      <FamilyPilotSurveyDialog presentation={rating} onClose={() => setRating(null)} onSaved={load} />
    </div>
  );
}
